import { Observable } from 'rxjs';
import httpService from './httpService';
import { UserAccount } from '../data/models/user-account.model';
import { ClientAdmin } from '../data/models/client.model';
import { PagedList } from '../data/models/paged-list.model';
import { SortModel } from '../data/models/sort-model.model';
import {
  usePortalName,
  usePortalNameFromClientBC,
} from '../hooks/usePortalName';
import securityUtils from './securityUtils';
import { usePortalClientBC } from '../hooks/usePortalClientBC';
import { UserRole } from '../data/models/user-role.model';

class UserService {
  private readonly apiBase;
  private readonly apiBaseMemberPortal;
  private readonly apiBaseClientPortal;
  private partnerName = 'standard';

  constructor() {
    const { __environment__ } = window as any;
    this.apiBase = __environment__.REACT_APP_USER_API_URL.trim();
    this.apiBaseMemberPortal =
      __environment__.REACT_APP_USER_API_URL_MEMBER_PORTAL.trim();
    this.apiBaseClientPortal =
      __environment__.REACT_APP_USER_API_URL_CLIENT_PORTAL.trim();
    const portalName = usePortalName();
    if (portalName) {
      this.partnerName = portalName;
    }
  }

  public setPartnerName(partnerName: string) {
    this.partnerName = partnerName;
  }

  public getPartnerName() {
    return this.partnerName;
  }

  private get apiUrl() {
    return this.partnerName === 'standard'
      ? `${this.apiBase}`
      : `${this.apiUrlMemberPortal}`;
  }

  private get apiUrlClientPortal() {
    if (this.partnerName === 'standard') {
      return `${this.apiBase}`;
    } else {
      return `${this.apiBaseClientPortal}`;
    }
  }

  private get apiUrlMemberPortal() {
    if (this.partnerName === 'standard') {
      return `${this.apiBase}`;
    } else {
      return `${this.apiBaseMemberPortal}`;
    }
  }

  public findUsers(
    pageSize: number,
    page: number,
    search: string,
    sortModel: SortModel | null,
    includeRoles: boolean = false,
    onboarding: boolean = false,
    roleIDs?: number[],
  ): Observable<PagedList<UserAccount>> {
    const portalName = usePortalName();
    const s = encodeURIComponent(search);
    const pwd = onboarding
      ? 'isPasswordTemporary=true'
      : 'isPasswordPermanent=true';

    let url = `${this.apiBase}?page=${page}&pageSize=${pageSize}&search=${s}&includeRoles=${includeRoles}&${pwd}`;
    if (portalName) {
      const clientBC = usePortalClientBC(portalName);
      if (clientBC) {
        url += `&clientBC=${clientBC}`;
      }
    }
    if (sortModel) {
      url += `&sort=${sortModel.column}&sortOrder=${sortModel.direction}`;
    }

    if (roleIDs && roleIDs.length > 0) {
      url += `&roleIDs=${roleIDs.join(',')}`;
    }

    return httpService.getJSON(url);
  }

  public getRoles(userCategoryTypeID: number): Observable<UserRole[]> {
    const url = `${this.apiBase}/roles?userCategoryTypeID=${userCategoryTypeID}`;
    return httpService.getJSON(url);
  }

  public getUserInfo(userid?: number): Observable<UserAccount> {
    const id = userid || 'me';
    const url = `${this.apiUrl}/${id}`;
    return httpService.getJSON(url);
  }

  public createUser(userData: UserAccount): Observable<UserAccount> {
    const url = `${this.apiUrl}`;
    return httpService.postJSON(url, userData);
  }

  public updateUser(userData: UserAccount): Observable<UserAccount> {
    const url = `${this.apiUrl}/${userData.userAccountID}`;
    // We should never try to pass the userAccountID in the body of the request
    const { userAccountID, ...newUserData } = userData;
    return httpService.putJSON(url, newUserData);
  }

  public deleteUser = (userId: number): Observable<void> => {
    const url = `${this.apiBase}/${userId}`;
    return httpService.deleteJSON(url);
  };

  public deleteClientUser = (userId: number): Observable<void> => {
    const url = `${this.apiBaseClientPortal}/${userId}`;
    return httpService.deleteJSON(url);
  };

  public deleteMemberUser = (userId: number): Observable<void> => {
    const url = `${this.apiBaseMemberPortal}/${userId}`;
    return httpService.deleteJSON(url);
  };

  public sendPasswordResetEmail(userId: number): Observable<void> {
    const url = `${this.apiUrl}/${userId}/reset-cognito-password`;
    return httpService.postJSON(url, {});
  }

  public sendAccountActivationEmail(userId: number): Observable<void> {
    const url = `${this.apiUrl}/${userId}/resend-cognito-activation-email`;
    return httpService.postJSON(url, {});
  }

  public validateUniqueEmail(email: string): Observable<boolean> {
    const url = `${this.apiUrl}/validate-unique?email=${encodeURIComponent(email)}`;
    return httpService.getJSON(url);
  }

  public getUserApiInfo(): Observable<string> {
    const apiUrl = this.apiUrl.split('users')?.[0];
    const url = `${apiUrl}/health-check`;
    return httpService.getJSON(url);
  }

  public onboardMemberUser(
    email: string,
    password: string,
    verifyEmailCallbackURL: string,
    clientLogoURL: string,
    clientBC: string,
  ) {
    const url = `${this.apiUrlMemberPortal}/sign-up`;
    const data = {
      emailAddress: email,
      permanentPassword: password,
      userCategoryTypeBC: 'PASSENGER',
      verifyEmailCallbackURL,
      clientLogoURL,
      clientBC,
    };
    return httpService.postJSON(url, data);
  }

  public onboardFederatedMemberUser(
    authorizationID: string,
    verifyEmailCallbackURL: string,
    clientLogoURL: string,
    clientBC: string,
  ) {
    const url = `${this.apiUrlMemberPortal}/sign-up`;
    const data = {
      authorizationID,
      userCategoryTypeBC: 'PASSENGER',
      verifyEmailCallbackURL,
      clientLogoURL,
      clientBC,
    };
    return httpService.postJSON(url, data);
  }

  public onboardFederatedUser(
    idToken: string,
    clientBC: string,
    memberPortal: boolean,
  ) {
    const base = memberPortal
      ? this.apiUrlMemberPortal
      : this.apiUrlClientPortal;
    const url = `${base}/sign-up-initiated`;
    const data = {
      authorizationID: idToken,
      clientBC,
    };
    return httpService.postJSON(url, data);
  }

  public associateMemberUserWithPassenger(
    userId: number,
    userFirstName: string,
    userLastName: string,
    memberID: number,
    dateOfBirth: string,
    phoneNumber: string,
    address: string,
    zipCode: string,
    clientBC: string,
  ) {
    const url = `${this.apiUrlMemberPortal}/${userId}/associate-passenger`;
    let data = {
      userFirstName,
      userLastName,
      memberID,
      dateOfBirth: String(dateOfBirth).split('T')[0],
      phoneNumber,
      address,
      zipCode,
      clientBC,
    };
    return httpService.postJSON(url, data);
  }

  public forgotMemberPassword(
    email: string,
    resetPasswordCallbackURL: string,
    clientLogoURL: string,
    clientBC: string,
  ): Observable<void> {
    const url = `${this.apiUrlMemberPortal}/forgot-cognito-password`;
    return httpService.postJSON(url, {
      email,
      resetPasswordCallbackURL,
      clientLogoURL,
      clientBC,
    });
  }

  public updatePassword(
    passwordResetToken: string,
    password: string,
  ): Observable<void> {
    const url = `${this.apiUrlMemberPortal}/forgot-change-password`;
    return httpService.postJSON(url, {
      passwordResetToken,
      password,
    });
  }

  public sendVerificationEmail(
    clientBC: string,
    verifyEmailCallbackURL: string,
    clientLogoURL: string,
  ): Observable<void> {
    const url = `${this.apiUrl}/send-email-verification`;
    return httpService.postJSON(url, {
      clientBC,
      verifyEmailCallbackURL,
      clientLogoURL,
    });
  }

  public verifyEmail(verificationToken: string): Observable<void> {
    const url = `${this.apiUrl}/verify-email-verification`;
    return httpService.postJSON(url, {
      verificationToken,
    });
  }

  public clientAdminVerifyToken(token: string) {
    const url = `${this.apiUrlMemberPortal}/verify-token?verificationToken=${encodeURIComponent(token)}`;
    return httpService.getJSON(url);
  }

  public clientAdminSetPassword(
    token: string,
    password: string,
    firstName: string,
    lastName: string,
  ) {
    const url = `${this.apiUrlClientPortal}/email-verify-password-set`;
    const data = {
      verificationToken: token,
      password: password,
      userFirstName: firstName,
      userLastName: lastName,
    };
    return httpService.postJSON(url, data);
  }

  public createClientAdmin(
    clientBC: string,
    clientAdminUserData: Partial<ClientAdmin>,
    userRoleIDs: number[],
  ): Observable<any> {
    const portalName = usePortalNameFromClientBC(clientBC);
    const clientLogoURL = securityUtils.getPortalLogoURL(portalName);
    const verifyEmailCallbackURL = `${securityUtils.getPortalCallbackURL(portalName)}/memberCreateAccount`;

    const url = `${this.apiBase.replace('users', 'partners/clientportal/users/client')}`;
    return httpService.postJSON(url, {
      ...clientAdminUserData,
      clientBC,
      userRoleIDs,
      clientLogoURL,
      verifyEmailCallbackURL,
    });
  }

  public resendClientAdminInvite(
    userAccountID: number,
    clientBC: string,
  ): Observable<any> {
    const portalName = usePortalNameFromClientBC(clientBC);
    const clientLogoURL = securityUtils.getPortalLogoURL(portalName);
    const verifyEmailCallbackURL = `${securityUtils.getPortalCallbackURL(portalName)}/memberCreateAccount`;

    const url = `${this.apiBase.replace('users', `partners/clientportal/users/${userAccountID}/resend-email-verify-password-set`)}`;
    return httpService.postJSON(url, {
      clientBC,
      clientLogoURL,
      verifyEmailCallbackURL,
    });
  }

  public getClientAdmins(
    clientBC: string,
    page: number,
    pageSize: number,
    userRoleIDs: number[],
  ): Observable<PagedList<ClientAdmin>> {
    const url = `${this.apiBase}?clientBC=${clientBC}&page=${page}&pageSize=${pageSize}&roleIDs=${userRoleIDs.join(',')}`;
    return httpService.getJSON(url);
  }

  public deleteClientAdmin(userId: number): Observable<any> {
    const url = `${this.apiUrlClientPortal.replace('users', 'partners/clientportal/users/')}/${userId}`;
    return httpService.deleteJSON(url);
  }

  public createClientPortalUser(
    clientBC: string,
    userData: Record<string, any>,
  ): Observable<any> {
    const portalName = usePortalNameFromClientBC(clientBC);
    const clientLogoURL = securityUtils.getPortalLogoURL(portalName);
    const verifyEmailCallbackURL = `${securityUtils.getPortalCallbackURL(portalName)}/memberCreateAccount`;

    const url = `${this.apiUrlClientPortal}/client`;
    const { roleId, ...rest } = userData;

    return httpService.postJSON(url, {
      ...rest,
      clientBC,
      userRoleIDs: [roleId],
      clientLogoURL,
      verifyEmailCallbackURL,
    });
  }

  public updateClientPortalUser(
    userAccountID: number,
    payload: any,
  ): Observable<any> {
    const url = `${this.apiUrlClientPortal}/${userAccountID}`;
    return httpService.putJSON(url, payload);
  }
}

const userService = new UserService();
export default userService;
